import { useMutation } from '@tanstack/react-query';
import { generateKeyBetween } from 'fractional-indexing';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { addQuoteV2SubitemMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2ItemWithTotalsResponse } from 'Services/Query/QuotesV2/Types';
import {
  findItemById,
  getItemId,
  updateItemInTree
} from '../utils/quote-helpers';
import { calculateGrossProfitPercentage } from '../utils/pricing-helpers';
import { useQuoteCache } from './use-quote-cache';

export interface AddFreehandSubitemVariables {
  itemId: string;
  name: string;
  sku: string;
  sell: number;
  cost: number;
  vendorName: string;
}

/** Build an optimistic QuoteProductDTO for a freehand sub-item */
const buildOptimisticFreehandSubitem = (
  vars: AddFreehandSubitemVariables,
  tempId: string
): QuoteProductDTO => {
  const { name, sku, sell, cost, vendorName, itemId } = vars;

  return {
    _id: tempId,
    id: tempId,
    type: 'product',
    productId: '',
    name,
    sku,
    quantity: 1,
    sell,
    cost,
    note: null,
    vendor: { name: vendorName, _id: null, vendorId: null },
    images: null,
    imageUrl: '',
    isFreeHand: true,
    isEditable: true,
    labelType: 'option',
    customLabel: '',
    includeInTotalCost: false,
    minimumQuoteQuantity: 1,
    overrideMinimumQuoteQuantity: true,
    children: [],
    parentID: itemId,
    totalGrossProfit: sell - cost,
    totalGrossProfitPercentage: calculateGrossProfitPercentage(cost, sell),
    description: '',
    packSize: '',
    unitOfMeasure: '',
    manufacturerSku: ''
  } as unknown as QuoteProductDTO;
};

/** Insert a sub-item into a parent product's children */
const addSubitemToParent = (
  items: any[],
  parentId: string,
  newItem: QuoteProductDTO
): any[] =>
  items.map(item => {
    if (getItemId(item) === parentId) {
      return {
        ...item,
        children: [...(item.children || []), newItem]
      };
    }
    if ('children' in item && item.children?.length) {
      return {
        ...item,
        children: addSubitemToParent(item.children, parentId, newItem)
      };
    }
    return item;
  });

/**
 * Add a freehand (SPO) sub-item to a parent product via the
 * dedicated v2 sub-item endpoint with isFreeHand=true.
 */
export const useAddFreehandSubitem = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = addQuoteV2SubitemMutation();

  return useMutation({
    mutationFn: (variables: AddFreehandSubitemVariables) => {
      const { itemId, name, sku, sell, cost, vendorName } = variables;
      const items = cache.getItems() || [];
      const parentItem = findItemById(items, itemId);

      const children = (parentItem as any)?.children || [];
      const lastPosition =
        children
          .map((c: any) => c.position as string | undefined)
          .filter(Boolean)
          .sort()
          .pop() ?? null;
      const position = generateKeyBetween(lastPosition, null);

      return mutationFn({
        quoteId,
        itemId,
        productId: '',
        name,
        sku,
        quantity: 1,
        sell,
        cost,
        position,
        isFreeHand: true,
        labelType: 'option',
        customLabel: '',
        includeInTotalCost: false,
        vendorName
      });
    },

    onMutate: async (variables: AddFreehandSubitemVariables) => {
      await cache.cancel();

      const previousItems = cache.getItems();

      // Compute position from pre-optimistic parent's children
      const parentItem = findItemById(previousItems || [], variables.itemId);
      const children = (parentItem as any)?.children || [];
      const lastPos =
        children
          .map((c: any) => c.position as string | undefined)
          .filter(Boolean)
          .sort()
          .pop() ?? null;
      const position = generateKeyBetween(lastPos, null);

      const tempId = `temp-${Date.now()}`;
      const newItem = {
        ...buildOptimisticFreehandSubitem(variables, tempId),
        position
      } as unknown as QuoteProductDTO;

      cache.setItems(old =>
        addSubitemToParent(old || [], variables.itemId, newItem)
      );

      return { previousItems, tempId };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data, _variables, context) => {
      const response = data as QuoteV2ItemWithTotalsResponse;
      const serverItem = response?.item;
      if (serverItem?._id && context?.tempId) {
        cache.setItems(old =>
          old
            ? updateItemInTree(old, context.tempId, {
                _id: serverItem._id,
                id: serverItem._id
              })
            : old
        );
      }
      if (response?.totals) {
        cache.setQuote(old =>
          old
            ? {
                ...old,
                totalSell: response.totals.totalSell,
                totalCost: response.totals.totalCost
              }
            : old
        );
      }
      cache.invalidate();
    }
  });
};
